import React from 'react';

/**
 * Page-level access rules (docs/Event_Materials_Feature.docx): one view
 * dropdown, one dependent download dropdown that only offers equal-or-stricter
 * audiences (+ "Downloads disabled"). Option keys mirror
 * EventArticleAccessService::VIEW_OPTIONS / DOWNLOAD_OPTIONS — the server
 * re-validates both the keys and the dependency.
 */
const VIEW_OPTIONS = [
    {
        value: 'public',
        rank: 0,
        label: 'Public',
        help: () => 'Anyone can view the materials, no login required.',
    },
    {
        value: 'authenticated',
        rank: 1,
        label: 'Logged-in users',
        help: () => 'Visible to anyone with a LegalOpsKPIs account, even if they are not registered for this event.',
    },
    {
        value: 'members_and_registrants',
        rank: 2,
        label: 'Chapter members + approved event registrants',
        help: (chapter) => `Visible to approved ${chapter ? `${chapter} chapter` : 'chapter'} members and approved registrants for this event.`,
    },
    {
        value: 'members_only',
        rank: 3,
        label: 'Chapter members only',
        help: (chapter) => `Visible only to approved members of the ${chapter ? `${chapter} chapter` : 'related chapter'}.`,
    },
    {
        value: 'registrants_only',
        rank: 4,
        label: 'Approved attendees only',
        help: () => 'Visible only to people approved for this specific event — chapter membership alone grants nothing.',
    },
];

const DOWNLOAD_DISABLED = {
    value: 'disabled',
    label: 'Downloads disabled',
    help: () => 'No one can download attachments (chairs & admins excepted).',
};

export const labelFor = (value) => {
    if (value === 'disabled') return DOWNLOAD_DISABLED.label;
    const option = VIEW_OPTIONS.find((o) => o.value === value);
    return option ? option.label : value;
};

const rankOf = (value) => {
    const option = VIEW_OPTIONS.find((o) => o.value === value);
    return option ? option.rank : 0;
};

// Chapter-only audiences don't apply to standalone workshops — there's no
// chapter to be a member of, so those two options never show for them.
const CHAPTER_ONLY_VALUES = ['members_and_registrants', 'members_only'];

export default function RulesCard({ rules, onChange, chapterName, savedAt }) {
    const viewOptions = chapterName
        ? VIEW_OPTIONS
        : VIEW_OPTIONS.filter((o) => !CHAPTER_ONLY_VALUES.includes(o.value));
    const viewOption = viewOptions.find((o) => o.value === rules.view)
        || viewOptions.find((o) => o.value === (chapterName ? 'members_and_registrants' : 'registrants_only'));
    const downloadChoices = [
        ...viewOptions.filter((o) => o.rank >= viewOption.rank),
        DOWNLOAD_DISABLED,
    ];
    const downloadOption = downloadChoices.find((o) => o.value === rules.download) || downloadChoices[0];

    const changeView = (view) => {
        // If the current download audience becomes broader than the new view
        // audience, tighten it to match (the server enforces this too).
        const download = rules.download !== 'disabled' && rankOf(rules.download) < rankOf(view)
            ? view
            : rules.download;
        onChange({ view, download });
    };

    return (
        <div className="ea-rules">
            <div className="ea-rules-grid">
                <div className="ea-rule-group">
                    <label className="ea-rule-label" htmlFor="ea-rule-view">Who can view the full materials?</label>
                    <select
                        id="ea-rule-view"
                        className="ea-rule-select"
                        value={viewOption.value}
                        onChange={(e) => changeView(e.target.value)}
                    >
                        {viewOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <p className="ea-rule-help">{viewOption.help(chapterName)}</p>
                </div>

                <div className="ea-rule-group">
                    <label className="ea-rule-label" htmlFor="ea-rule-download">Who can download attachments?</label>
                    <select
                        id="ea-rule-download"
                        className="ea-rule-select"
                        value={downloadOption.value}
                        onChange={(e) => onChange({ view: rules.view, download: e.target.value })}
                    >
                        {downloadChoices.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <p className="ea-rule-help">{downloadOption.help(chapterName)}</p>
                </div>
            </div>
            {savedAt && <p className="ea-save-flash">Rules saved ✓ {savedAt}</p>}
        </div>
    );
}
